import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ScrollArea } from "@shadcn-in-nx/ui/scroll-area";
import { Button } from "@shadcn-in-nx/ui/button";
import { RefreshCw, Activity, ChevronDown } from "lucide-react";
import { useBluelagoon } from "@bluelagoon";
import ActivityStatusBanner from "./ActivityStatusBanner";
import ActivityFilters from "./ActivityFilters";
import ActivityStream from "./ActivityStream";
import ActivityDetailPanel from "./ActivityDetailPanel";
import { buildFetchParams } from "./activityFilterUtils";
import { computeStatsFromInvocations } from "./activityStatsFallback";

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

function ActivityPanel({ workflowId, stepId }) {
    const { t } = useTranslation();
    const [selectedInvocation, setSelectedInvocation] = useState(null);
    const [filters, setFilters] = useState({ status: "all", timeRange: "1h", pathSearch: "" });
    const [page, setPage] = useState(1);
    const [accumulated, setAccumulated] = useState([]);
    const [hasMore, setHasMore] = useState(false);
    const searchTimer = useRef(null);

    const { call: fetchStats, response: statsResponse, isLoading: statsLoading } =
        useBluelagoon({ method: "getFunctionInvocationStats" });

    const { call: fetchInvocations, isLoading: invocationsLoading } =
        useBluelagoon({ method: "getFunctionInvocations" });

    const loadPage = useCallback(
        async (targetPage, append) => {
            const params = buildFetchParams(filters, { stepId, page: targetPage, pageSize: PAGE_SIZE });
            try {
                const result = await fetchInvocations(params);
                const rows = result?.data?.invocations || [];
                setAccumulated((prev) => (append ? [...prev, ...rows] : rows));
                setHasMore(!!result?.data?.hasMore);
            } catch (err) {
                // Backend returns { status: 500, error: '...' } via reject.
                // useBluelagoon already stores it in `error`; we just log for debugging
                // and let the UI render the error state on the next render.
                // eslint-disable-next-line no-console
                console.warn("[Activity] Failed to load invocations:", err);
                if (!append) setAccumulated([]);
                setHasMore(false);
            }
        },
        [filters, stepId, fetchInvocations]
    );

    // Reset + reload whenever step or filters change. Debounce the path search.
    useEffect(() => {
        if (searchTimer.current) clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => {
            fetchStats();
            setPage(1);
            loadPage(1, false);
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(searchTimer.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stepId, filters.status, filters.timeRange, filters.pathSearch]);

    const handleRefresh = useCallback(() => {
        fetchStats();
        setPage(1);
        loadPage(1, false);
    }, [fetchStats, loadPage]);

    const handleLoadMore = useCallback(() => {
        const next = page + 1;
        setPage(next);
        loadPage(next, true);
    }, [page, loadPage]);

    const realtimeStats = statsResponse?.data;
    const stats = useMemo(() => {
        if (realtimeStats && realtimeStats.totalInvocations > 0) return realtimeStats;
        return computeStatsFromInvocations(accumulated) || realtimeStats;
    }, [realtimeStats, accumulated]);

    if (selectedInvocation) {
        return (
            <ActivityDetailPanel
                invocation={selectedInvocation}
                onBack={() => setSelectedInvocation(null)}
            />
        );
    }

    return (
        <ScrollArea className="h-full bg-gray-50">
            <div className="p-4 md:p-6 pb-8">
                <div className="max-w-4xl mx-auto space-y-6">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex items-start justify-between gap-4">
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
                                <Activity className="w-5 h-5 text-blue-600" />
                                {t("activity.title")}
                            </h2>
                            <p className="text-sm text-gray-500">
                                {t("activity.subtitle") || "Every request to your API functions, in real time."}
                            </p>
                        </div>
                        <Button variant="ghost" size="sm" className="h-8 px-3 text-xs text-gray-600 hover:text-gray-900" onClick={handleRefresh} disabled={statsLoading || invocationsLoading}>
                            <RefreshCw className={`w-3 h-3 mr-1.5 ${statsLoading || invocationsLoading ? "animate-spin" : ""}`} />
                            {t("common.refresh") || "Refresh"}
                        </Button>
                    </div>
                    <ActivityStatusBanner stats={stats} loading={statsLoading && accumulated.length === 0} />
                    <ActivityFilters filters={filters} onChange={setFilters} />
                    <ActivityStream
                        invocations={accumulated}
                        loading={invocationsLoading && accumulated.length === 0}
                        onSelect={setSelectedInvocation}
                    />
                    {hasMore && accumulated.length > 0 && (
                        <div className="flex justify-center">
                            <Button variant="outline" size="sm" onClick={handleLoadMore} disabled={invocationsLoading}>
                                <ChevronDown className={`w-3.5 h-3.5 mr-1.5 ${invocationsLoading ? "animate-bounce" : ""}`} />
                                {invocationsLoading ? (t("activity.loading") || "Loading…") : (t("activity.viewMore") || "View more")}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </ScrollArea>
    );
}

export default ActivityPanel;
